import { useEffect, useRef, useState } from "react";
import DatePicker from "../../../../components/DatePicker";
import TWButton from "../../../../components/TWButton";

interface DateRangeDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (dateRange: DateRange) => void;
  initialDateRange: DateRange;
  anchorRef: React.RefObject<HTMLButtonElement>;
}

export interface DateRange {
  from?: string;
  to?: string;
}

const DateRangeDropdown = ({
  isOpen,
  onClose,
  onApply,
  initialDateRange,
  anchorRef,
}: DateRangeDropdownProps) => {
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const handleApply = () => {
    // Remove undefined values and convert to ISO datetime format
    const cleanedDateRange: DateRange = {};
    if (dateRange.from) {
      // Convert YYYY-MM-DD to ISO datetime (start of day)
      cleanedDateRange.from = new Date(dateRange.from + "T00:00:00").toISOString();
    }
    if (dateRange.to) {
      // Convert YYYY-MM-DD to ISO datetime (end of day)
      cleanedDateRange.to = new Date(dateRange.to + "T23:59:59").toISOString();
    }
    
    onApply(cleanedDateRange);
    onClose();
  };

  const handleClear = () => {
    setDateRange({});
  };

  const handleQuickFilter = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);

    // Store as YYYY-MM-DD for the date inputs
    setDateRange({
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    });
  };

  const hasDateRange = dateRange.from || dateRange.to;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="font-glacial font-semibold text-sm uppercase tracking-wide">
          Date Added
        </h3>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-4">
        {/* Date Inputs */}
        <div className="space-y-3">
          <div>
            <label className="block font-glacial font-medium text-xs uppercase tracking-wide text-gray-600 mb-1.5">
              From Date
            </label>
            <DatePicker
              value={dateRange.from || ""}
              onChange={(date) =>
                setDateRange({ ...dateRange, from: date })
              }
              placeholder="Select start date"
            />
          </div>

          <div>
            <label className="block font-glacial font-medium text-xs uppercase tracking-wide text-gray-600 mb-1.5">
              To Date
            </label>
            <DatePicker
              value={dateRange.to || ""}
              onChange={(date) =>
                setDateRange({ ...dateRange, to: date })
              }
              placeholder="Select end date"
            />
          </div>
        </div>

        {/* Quick Filters */}
        <div>
          <h4 className="font-glacial font-medium text-xs uppercase tracking-wide text-gray-600 mb-2">
            Quick Filters
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleQuickFilter(7)}
              className="px-3 py-1.5 border border-gray-300 rounded font-glacial text-xs hover:bg-gray-50"
            >
              Last 7 days
            </button>
            <button
              onClick={() => handleQuickFilter(30)}
              className="px-3 py-1.5 border border-gray-300 rounded font-glacial text-xs hover:bg-gray-50"
            >
              Last 30 days
            </button>
            <button
              onClick={() => handleQuickFilter(90)}
              className="px-3 py-1.5 border border-gray-300 rounded font-glacial text-xs hover:bg-gray-50"
            >
              Last 3 months
            </button>
            <button
              onClick={() => handleQuickFilter(180)}
              className="px-3 py-1.5 border border-gray-300 rounded font-glacial text-xs hover:bg-gray-50"
            >
              Last 6 months
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex gap-2">
        <TWButton
          onClick={handleClear}
          variant="secondary"
          className="flex-1 h-9 text-xs"
          disabled={!hasDateRange}
        >
          CLEAR
        </TWButton>
        <TWButton
          onClick={handleApply}
          variant="primary"
          className="flex-1 h-9 text-xs"
        >
          APPLY
        </TWButton>
      </div>
    </div>
  );
};

export default DateRangeDropdown;
